import { get, find, uniq, isEmpty } from 'lodash';

import { serializeClient } from './client';
import { serializePerson } from './person';
import { serializeOrders } from './orders';
import { serializeTagsToMap } from './tag';
import { serializeExcessTicketMessages } from './message';
import { serializeChainType } from './chain';
import { EMPTY_PLAN_REASON } from '../../consts/defaultPlanReason';
import { STATUSES, EXTRA_STATUSES } from '../../consts/tickets';
import { TYPES } from '@components/CRM/Tickets/TicketType';
import { SUBTYPES } from '@components/CRM/Tickets/TicketType/constants';
import { FILTER_NAMES } from '../../components/CRM/Tickets/TicketFilters/constants';

import convertDateToServerFormat from '../../utils/convertDateToServerFormat';

export const serializeTicket = ({
  ticket: rawTicket,
  persons = [],
  tags: tagOptions = [],
  orders = [],
} = {}) => {
  const ticket = rawTicket || {};
  const ticketId = String(get(ticket, 'id'));
  const clientId = get(ticket, 'client_id');
  const client = find(persons, { id: clientId });
  const serializedClient = serializeClient(client);

  const managerId = get(ticket, 'manager_id');
  const manager = find(persons, { id: managerId });
  const serializedManager = serializePerson(manager);
  const serializedOrders = serializeOrders(orders);

  const unparsedType = get(ticket, 'kind');
  const unparsedSubtype = get(ticket, 'subkind');
  const type = Object.values(TYPES).includes(unparsedType)
    ? unparsedType
    : TYPES.UNKNOWN;
  let subtype;
  if (Object.values(SUBTYPES).includes(unparsedSubtype)) {
    subtype = unparsedSubtype;
  }

  const tagIds = get(ticket, 'tag_ids');
  const tagsMap = serializeTagsToMap(tagOptions);
  const tags = tagIds && tagIds.map(id => tagsMap[id]?.title || '');

  const orderIdsMap = get(ticket, 'order_ids');
  const orderIds = orderIdsMap && orderIdsMap.map(id => String(id));

  const status = get(ticket, 'status');
  const isClosed = [
    STATUSES.CLOSED,
    EXTRA_STATUSES.CLOSED_BY_ORDER,
    EXTRA_STATUSES.CLOSED_BY_REASON,
    EXTRA_STATUSES.CLOSE_ACTION,
  ].includes(status);

  const timeFieldsNameResolver = {
    created_at: 'createdAt',
    status_changed_at: 'statusChangedAt',
    last_answered_at: 'lastAnsweredAt',
    planned_at: 'plannedAt',
    assigned_at: 'assignedAt',
    updated_at: 'updatedAt',
  };

  const sla = get(ticket, 'sla');
  const ltt = get(ticket, 'ltt');

  const serializedTicket = {
    id: ticketId,
    client: ticket.client_id && String(ticket.client_id),
    mailGroup: ticket.mail_group_id && String(ticket.mail_group_id),
    manager: ticket.manager_id && String(ticket.manager_id),
    closeReason: ticket.close_reason_id && String(ticket.close_reason_id),
    reason: ticket.reason_id && String(ticket.reason_id),
    menuSource: ticket.menu_source_id && String(ticket.menu_source_id),
    source: get(ticket, 'source'),
    read: get(ticket, 'is_read'),
    status: {
      priority: get(ticket, 'priority'),
      time: get(ticket, get(ticket, 'sort_field_name')),
      title: get(ticket, 'status'),
    },
    subject: get(ticket, 'subject'),
    subtype,
    subAgentName: get(ticket, 'sub_agent_name'),
    hasPriorityService: !isClosed && get(ticket, 'has_priority_service'),
    template: null,
    notificationChannel: null,
    notificationMessage: null,
    type,
    unreadEmailAmount: get(ticket, 'unread_emails_count'),
    unreadMessengerAmount: get(ticket, 'unread_messages_count'),
    ruleIds: get(ticket, 'rule_ids'),
    orderIds,
    [timeFieldsNameResolver.created_at]: get(ticket, 'created_at'),
    [timeFieldsNameResolver.status_changed_at]: get(
      ticket,
      'status_changed_at'
    ),
    [timeFieldsNameResolver.last_answered_at]: get(ticket, 'last_answered_at'),
    [timeFieldsNameResolver.planned_at]: get(ticket, 'planned_at'),
    [timeFieldsNameResolver.assigned_at]: get(ticket, 'assigned_at'),
    [timeFieldsNameResolver.updated_at]: get(ticket, 'updated_at'),
    statusWeight: get(ticket, 'status_weight'),
    plannedReasonId:
      (ticket.planned_reason_id && String(ticket.planned_reason_id)) ||
      EMPTY_PLAN_REASON.id,
    isClosed,
    isAssigned: get(ticket, 'reserved'),
    sla,
    ltt,
    info: null,
    ...(tags ? { tags: uniq(tags) } : {}),
  };

  return {
    id: ticketId,
    client: serializedClient,
    ticket: serializedTicket,
    manager: serializedManager,
    ownOrders: serializedOrders,
  };
};

export const serializeTickets = ({
  tickets,
  persons = [],
  tags = [],
  orders = [],
}) => {
  if (isEmpty(tickets)) {
    return [];
  }
  return tickets.map(ticket =>
    serializeTicket({
      ticket,
      persons,
      tags,
      orders,
    })
  );
};

// Данный сериалайзер используем при запросе всех тикетов, т.к. инфа о менеджере в каждом тикете в сторе не нужна
export const serializeTicketsWithoutManagers = ({
  tickets,
  persons = [],
  tags = [],
  orders = [],
}) => {
  if (isEmpty(tickets)) {
    return [];
  }
  return tickets.map(ticket => {
    const serializedTicket = serializeTicket({
      ticket,
      persons,
      tags,
      orders,
    });
    delete serializedTicket.manager;

    return serializedTicket;
  });
};

export const serializeTicketsToMap = ({
  tickets,
  persons = [],
  tags = [],
  orders = [],
}) => {
  if (isEmpty(tickets)) {
    return {};
  }

  return tickets.reduce((acc, ticket) => {
    const ticketId = ticket.id.toString();
    return ticketId
      ? {
          [ticketId]: serializeTicket({
            ticket,
            persons,
            tags,
            orders,
          }),
          ...acc,
        }
      : acc;
  }, {});
};

export const searializeTicketFilters = filters => {
  const filteredFilters = { ...filters };
  const dates = filteredFilters[FILTER_NAMES.DEPARTURE_DATE];
  const sortByNew = filteredFilters[FILTER_NAMES.SORT] === 'DESC';

  delete filteredFilters[FILTER_NAMES.DEPARTURE_DATE];
  delete filteredFilters[FILTER_NAMES.SORT];
  return {
    ...filteredFilters,
    ...(dates && {
      departure_date_from: convertDateToServerFormat(dates[0]),
      departure_date_to: convertDateToServerFormat(dates[1]),
    }),
    new: sortByNew,
  };
};

export const serializeActiveTicketInfo = info => {
  const { manager_ids } = info;

  return {
    viewingManagersIds: manager_ids,
  };
};

export const serializeExcessTickets = excessTickets =>
  (excessTickets || []).map(ticket => ({
    id: String(ticket.id),
    subject: ticket.subject,
    status: ticket.status,
    mailGroupId: ticket.mail_group_id && String(ticket.mail_group_id),
    createdAt: ticket.created_at,
    updatedAt: ticket.updated_at,
    chain: ticket.chain && {
      id: String(ticket.chain.id),
      subject: ticket.chain.subject,
      service: ticket.chain.kind,
      type: serializeChainType(ticket.chain.kind),
      messages: serializeExcessTicketMessages(ticket.chain.messages),
    },
  }));
